import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/app/api";
import { useAppDispatch } from "@/app/hooks";
import { Checkbox } from "@/components/ui/Field";
import {
  isSignedIn,
  rememberedUsername,
  safeReturnPath,
  startSession,
  tokensToSession,
} from "@/lib/auth";
import { emailProblem } from "@/lib/validation";
import { useLoginMutation } from "../auth-api";
import { authErrorMessage } from "../auth-messages";
import {
  AuthHeading,
  AuthLayout,
  EmailField,
  FormAlert,
  PasswordField,
  SubmitButton,
} from "./AuthParts";

/**
 * The way in: email and password, which return the tokens directly.
 *
 * The frame is shared with forgot-password; see `AuthParts`.
 */
export function SignInPage() {
  const [params] = useSearchParams();

  // Where the guard (or an expired session) bounced from, so a link into a
  // screen survives the sign-in instead of dumping everyone on the dashboard.
  const from = safeReturnPath(params.get("from"));

  // An already-signed-in tab that lands here — a bookmark, a back button —
  // goes where it was headed. Rendering the form over a live session invites
  // signing in as someone else by accident.
  if (isSignedIn()) return <Navigate to={from} replace />;

  return (
    <AuthLayout>
      <SignInForm
        to={from}
        initialEmail={params.get("email")}
        // Set by the reset screen, which deliberately does not sign anyone in.
        notice={params.get("reset") === "1" ? "Password changed. Sign in with the new one." : null}
      />
    </AuthLayout>
  );
}

function SignInForm({
  to,
  initialEmail,
  notice,
}: {
  to: string;
  initialEmail: string | null;
  notice: string | null;
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [login, loginState] = useLoginMutation();

  const remembered = rememberedUsername();
  const [email, setEmail] = useState(initialEmail ?? remembered ?? "");
  const [password, setPassword] = useState("");
  // Ticked by default, so closing the browser does not sign anyone out. On a
  // shared machine, untick it and the session ends with the browser.
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    try {
      const signedIn = await login({ email: email.trim(), password }).unwrap();
      // Anything the previous occupant of this tab cached is keyed to their
      // token. Resetting is cheaper and more certain than hunting for the tags
      // that would invalidate it.
      dispatch(api.util.resetApiState());
      startSession(
        { admin_id: signedIn.admin.id, username: email.trim(), ...tokensToSession(signedIn) },
        remember,
      );
      void navigate(to, { replace: true });
    } catch (err) {
      setError(authErrorMessage(err, "password"));
      setPassword("");
    }
  };

  return (
    <>
      <AuthHeading title="Sign In">Use your admin email and password.</AuthHeading>

      <form onSubmit={(event) => void submit(event)} noValidate className="space-y-4">
        <EmailField value={email} onChange={setEmail} autoFocus={email === ""} />

        <PasswordField
          label="Password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          autoFocus={email !== ""}
        />

        <Checkbox
          label="Keep me signed in"
          checked={remember}
          onChange={setRemember}
          className="-ml-2"
        />

        <FormAlert error={error} notice={notice} />

        <SubmitButton
          busy={loginState.isLoading}
          disabled={emailProblem(email) !== null || password === ""}
        >
          Sign In
        </SubmitButton>
      </form>
    </>
  );
}
